import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import {
  getWaAutoReplies, createWaAutoReply, updateWaAutoReply,
  deleteWaAutoReply, toggleWaAutoReply, testWaAutoReply, getWaAccounts
} from '../../../services/api';
import { toast } from 'sonner';
import {
  Bot, Plus, Edit2, Trash2, RefreshCw, ToggleLeft, ToggleRight,
  FlaskConical, X, ChevronLeft, MessageCircle, Inbox, FileText,
  Zap, Bell, Hash, Users, Settings
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const TRIGGER_TYPES = [
  { value: 'keyword', label: 'Palabra clave' },
  { value: 'contains', label: 'Contiene' },
  { value: 'exact', label: 'Exacto' },
  { value: 'regex', label: 'Regex' },
];

const EMPTY_FORM = {
  name: '', trigger_type: 'contains', trigger_value: '',
  response_text: '', priority: 100, account_id: '',
};

const WhatsAppAutoReplies = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testModal, setTestModal] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWaAutoReplies();
      setRules(res.data?.rules || []);
    } catch { toast.error('Error cargando reglas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadRules();
    getWaAccounts().then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
  }, [loadRules]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      name: rule.name || '',
      trigger_type: rule.trigger_type || 'contains',
      trigger_value: rule.trigger_value || '',
      response_text: rule.response_text || '',
      priority: rule.priority ?? 100,
      account_id: rule.account_id || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.trigger_value || !form.response_text) {
      toast.error('Completa nombre, trigger y respuesta');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, account_id: form.account_id || null };
      if (editing) {
        await updateWaAutoReply(editing.id, payload);
        toast.success('Regla actualizada');
      } else {
        await createWaAutoReply(payload);
        toast.success('Regla creada');
      }
      setShowModal(false);
      loadRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Eliminar regla "${rule.name}"?`)) return;
    try {
      await deleteWaAutoReply(rule.id);
      toast.success('Regla eliminada');
      loadRules();
    } catch { toast.error('Error eliminando'); }
  };

  const handleToggle = async (rule) => {
    try {
      const res = await toggleWaAutoReply(rule.id);
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: res.data.is_active } : r));
    } catch { toast.error('Error cambiando estado'); }
  };

  const handleTest = async () => {
    if (!testMsg.trim()) return;
    setTesting(true);
    try {
      const res = await testWaAutoReply({ message: testMsg });
      setTestResult(res.data);
    } catch { toast.error('Error probando'); }
    finally { setTesting(false); }
  };

  const getAccountName = (id) => accounts.find(a => a.id === id)?.display_name || 'Todas';

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = item.path === '/superadmin/whatsapp/auto-replies';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  active ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Bot className="w-5 h-5 text-purple-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Auto-respuestas</h1>
              <p className="text-sm text-slate-500">Reglas de respuesta automatica por keywords</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setTestModal(true); setTestResult(null); setTestMsg(''); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-100 text-amber-700 text-sm rounded-lg hover:bg-amber-200">
              <FlaskConical className="w-4 h-4" /> Probar
            </button>
            <button onClick={openCreate}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700">
              <Plus className="w-4 h-4" /> Nueva Regla
            </button>
            <button onClick={loadRules}
              className="p-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Cargando...</div>
          ) : rules.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Bot className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay reglas de auto-respuesta</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-slate-600">Nombre</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Tipo</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Trigger</th>
                    <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Respuesta</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Prioridad</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Cuenta</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Matches</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Estado</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rules.map((rule) => (
                    <tr key={rule.id} className={`hover:bg-slate-50 ${!rule.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{rule.name}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {TRIGGER_TYPES.find(t => t.value === rule.trigger_type)?.label || rule.trigger_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 max-w-[150px] truncate">{rule.trigger_value}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate hidden lg:table-cell">{rule.response_text}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{rule.priority}</td>
                      <td className="px-4 py-3 text-center text-xs text-slate-500">{getAccountName(rule.account_id)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                          {rule.match_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleToggle(rule)} className="text-slate-500 hover:text-slate-700">
                          {rule.is_active
                            ? <ToggleRight className="w-6 h-6 text-green-500" />
                            : <ToggleLeft className="w-6 h-6 text-slate-400" />
                          }
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(rule)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(rule)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">
                {editing ? 'Editar Regla' : 'Nueva Regla Auto-respuesta'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Saludo inicial" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de trigger *</label>
                  <select value={form.trigger_type} onChange={e => setForm(p => ({ ...p, trigger_type: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prioridad</label>
                  <input type="number" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Valor del trigger *</label>
                <input value={form.trigger_value} onChange={e => setForm(p => ({ ...p, trigger_value: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder={form.trigger_type === 'regex' ? '^hola.*mundo$' : 'hola'} />
                <p className="text-xs text-slate-400 mt-1">
                  {form.trigger_type === 'keyword' && 'Palabra exacta dentro del mensaje (separada por espacios)'}
                  {form.trigger_type === 'contains' && 'Subcadena que debe existir en el mensaje'}
                  {form.trigger_type === 'exact' && 'El mensaje debe ser exactamente este texto'}
                  {form.trigger_type === 'regex' && 'Expresion regular (case-insensitive)'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Texto de respuesta *</label>
                <textarea value={form.response_text} onChange={e => setForm(p => ({ ...p, response_text: e.target.value }))}
                  rows={4}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Hola! Gracias por contactarnos..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cuenta (opcional)</label>
                <select value={form.account_id} onChange={e => setForm(p => ({ ...p, account_id: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                  <option value="">Todas las cuentas</option>
                  {accounts.filter(a => a.status === 'active').map(a => (
                    <option key={a.id} value={a.id}>{a.display_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {testModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Probar Auto-respuesta</h2>
              <button onClick={() => setTestModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mensaje de prueba</label>
                <input value={testMsg} onChange={e => setTestMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleTest()}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="hola, quiero informacion" />
              </div>
              <button onClick={handleTest} disabled={testing || !testMsg.trim()}
                className="w-full px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {testing ? 'Probando...' : 'Probar'}
              </button>
              {testResult && (
                <div className={`p-3 rounded-lg border ${testResult.matched ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                  {testResult.matched ? (
                    <>
                      <p className="text-sm font-medium text-green-700">Match encontrado</p>
                      <p className="text-xs text-green-600 mt-1">Regla: {testResult.rule?.name}</p>
                      <p className="text-xs text-green-600">Respuesta: {testResult.rule?.response_text}</p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">Ningun match encontrado</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default WhatsAppAutoReplies;
